import React, { useState, useEffect } from 'react';
import {
  FileText,
  Search,
  XCircle,
  X,
  Eye,
  Download,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { api, API_BASE_URL } from './services/apiClient';

// Mock data — fallback cuando el backend no está disponible
const mockSystemReports = [
  {
    id: 1,
    name: 'CartografiaBase-001-R-001',
    plantilla: 'Cartografía Base',
    fechaEjecucion: '2024-06-10',
    estado: 'Aprobado',
    totalValidaciones: 45,
    validacionesExitosas: 45,
    validacionesFalladas: 0,
    createdBy: 'user',
    _isMockData: true
  },
  {
    id: 2,
    name: 'CatastroUrbano-002-R-001',
    plantilla: 'Catastro Urbano',
    fechaEjecucion: '2024-06-08',
    estado: 'Aprobado con advertencias',
    totalValidaciones: 32,
    validacionesExitosas: 30,
    validacionesFalladas: 2,
    createdBy: 'admin',
    _isMockData: true
  },
  {
    id: 3,
    name: 'InfraestructuraVial-003-R-001',
    plantilla: 'Infraestructura Vial',
    fechaEjecucion: '2024-06-05',
    estado: 'No aprobado',
    totalValidaciones: 28,
    validacionesExitosas: 25,
    validacionesFalladas: 3,
    createdBy: 'user',
    _isMockData: true
  }
];

// Estilo del badge según veredicto
const getVeredictoStyle = (estado) => {
  switch (estado) {
    case 'Aprobado':
      return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', Icon: CheckCircle };
    case 'Aprobado con advertencias':
      return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', Icon: AlertTriangle };
    case 'No aprobado':
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', Icon: XCircle };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', Icon: AlertCircle };
  }
};

const ReportComparisonModule = ({ useAuth, usePermissions, useNotification, BaseButton, BaseInput, BaseCard }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addNotification: showNotification } = useNotification();

  const [systemReports, setSystemReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  // Filtros
  const [reportNameFilter, setReportNameFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState(user?.role === 'admin' ? 'organization' : 'my');

  // Estados de UI
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // Modales
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const canCompareReports = hasPermission('compare_reports') || user?.role === 'admin';
  const canViewOrgReports = user?.role === 'admin' || hasPermission('view_organization_reports');

  // ── Carga desde API ──────────────────────────────────────────────────────────
  const loadReports = async () => {
    setInitialLoading(true);
    try {
      const viewAll = canViewOrgReports && ownerFilter === 'organization';
      const data = await api.get(`/api/reports/generated?all=${viewAll}`);
      setSystemReports(Array.isArray(data) ? data : []);
      setIsUsingMockData(false);
    } catch {
      setSystemReports(mockSystemReports);
      setIsUsingMockData(true);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (canCompareReports) loadReports();
  }, [ownerFilter, canViewOrgReports]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtros locales ──────────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = [...systemReports];

    if (reportNameFilter.trim())
      filtered = filtered.filter(r => r.name.toLowerCase().includes(reportNameFilter.toLowerCase()));

    if (templateFilter.trim())
      filtered = filtered.filter(r => r.plantilla.toLowerCase().includes(templateFilter.toLowerCase()));

    if (dateFilter)
      filtered = filtered.filter(r => r.fechaEjecucion === dateFilter);

    // Con mock data o sin permiso de organización, filtrar por usuario propio
    if (isUsingMockData && ownerFilter === 'my')
      filtered = filtered.filter(r => r.createdBy === user?.username);
    else if (!canViewOrgReports)
      filtered = filtered.filter(r => r.createdBy === user?.username);

    setFilteredReports(filtered);
  }, [systemReports, reportNameFilter, templateFilter, dateFilter, ownerFilter, user, canViewOrgReports, isUsingMockData]);

  const clearFilters = () => {
    setReportNameFilter('');
    setTemplateFilter('');
    setDateFilter('');
    setOwnerFilter('my');
  };

  // ── Acciones ─────────────────────────────────────────────────────────────────
  const handleDelete = (report) => {
    if (!hasPermission('delete_reports')) {
      showNotification('No tienes permisos para eliminar informes.', 'error');
      return;
    }
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    setLoading(true);
    try {
      if (!isUsingMockData) await api.del(`/api/reports/generated/${reportToDelete.id}`);
      setSystemReports(prev => prev.filter(r => r.id !== reportToDelete.id));
      showNotification('Informe eliminado exitosamente', 'success');
    } catch {
      showNotification('Error al eliminar el informe', 'error');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setReportToDelete(null);
    }
  };

  const handleDownload = async (report, inline = false) => {
    if (isUsingMockData) {
      showNotification('No hay datos reales disponibles para descargar.', 'warning');
      return;
    }
    setDownloadingId(report.id);
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL()}/api/reports/generated/${report.id}/download${inline ? '?inline=true' : ''}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error al obtener el informe');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (inline) {
        window.open(objectUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `Informe_${report.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        showNotification('Informe descargado exitosamente.', 'success');
      }
    } catch {
      showNotification('Error al descargar el informe.', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  if (!canCompareReports) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">No tiene permisos para acceder al módulo de informes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Explorador de Informes</h1>
          <p className="text-sm text-gray-500 mt-0.5">Informes PDF oficiales generados por las ejecuciones de validación.</p>
        </div>
        <BaseButton variant="outline" onClick={loadReports} disabled={initialLoading} title="Recargar">
          <RefreshCw size={15} className={initialLoading ? 'animate-spin' : ''} />
        </BaseButton>
      </div>

      {/* Indicador mock */}
      {isUsingMockData && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
          Mostrando datos de ejemplo — el servidor no está disponible.
        </div>
      )}

      {/* Filtros */}
      <BaseCard className="mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <BaseInput
            label="Nombre del informe"
            placeholder="Buscar por nombre..."
            value={reportNameFilter}
            onChange={(e) => setReportNameFilter(e.target.value)}
            icon={Search}
          />
          <BaseInput
            label="Plantilla"
            placeholder="Buscar por plantilla..."
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
          />
          <BaseInput
            label="Fecha de generación"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          <div className="flex gap-2 items-end">
            {canViewOrgReports && (
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="my">Mis informes</option>
                <option value="organization">Toda la organización</option>
              </select>
            )}
            <BaseButton variant="outline" onClick={clearFilters} title="Limpiar filtros">
              <X size={15} />
            </BaseButton>
          </div>
        </div>
      </BaseCard>

      {/* Contenido */}
      {initialLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 size={28} className="animate-spin mr-3" />
          <span>Cargando informes...</span>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText size={44} className="mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-400">No hay informes disponibles</p>
          <p className="text-sm mt-1">
            {systemReports.length > 0
              ? 'Ajuste los filtros para ver resultados.'
              : 'Genere un informe oficial desde el detalle de una ejecución de validación.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredReports.map((report) => {
            const vStyle = getVeredictoStyle(report.estado);
            const VIcon = vStyle.Icon;
            return (
              <div
                key={report.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Cuerpo de la tarjeta */}
                <div className="p-4 flex-1">
                  {/* Veredicto badge */}
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${vStyle.bg} ${vStyle.text} ${vStyle.border}`}>
                    <VIcon size={11} />
                    {report.estado}
                  </span>

                  {/* Nombre del informe */}
                  <p className="mt-2 font-semibold text-gray-900 text-sm leading-tight break-all" title={report.name}>
                    {report.name}
                  </p>

                  {/* Plantilla */}
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <FileText size={11} className="flex-shrink-0" />
                    {report.plantilla}
                  </p>

                  {/* Detalles */}
                  <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Fecha generación:</span>
                      <span className="font-medium text-gray-700">{report.fechaEjecucion}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total validaciones:</span>
                      <span className="font-medium text-gray-700">{report.totalValidaciones}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Generado por:</span>
                      <span className="font-medium text-gray-700 truncate max-w-[120px]" title={report.createdBy}>
                        {report.createdBy}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer con botones */}
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 flex justify-end gap-1.5">
                  <BaseButton
                    variant="outline"
                    size="sm"
                    icon={Eye}
                    onClick={() => { setSelectedReport(report); setShowDetailModal(true); }}
                    title="Ver detalles"
                  />
                  <BaseButton
                    variant="outline"
                    size="sm"
                    icon={downloadingId === report.id ? Loader2 : Download}
                    onClick={() => handleDownload(report)}
                    disabled={downloadingId === report.id}
                    title="Descargar PDF"
                  />
                  {hasPermission('delete_reports') && (
                    <BaseButton
                      variant="danger"
                      size="sm"
                      icon={Trash2}
                      onClick={(e) => { e.stopPropagation(); handleDelete(report); }}
                      title="Eliminar informe"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal eliminar */}
      {showDeleteModal && reportToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Eliminar Informe</h3>
              <p className="mt-2 text-sm text-gray-500">
                ¿Estás seguro de que quieres eliminar <strong>{reportToDelete.name}</strong>?
                Se eliminará el registro y el archivo PDF. Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="bg-gray-50 px-4 py-3 flex flex-row-reverse gap-3 rounded-b-lg">
              <BaseButton variant="danger" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Eliminando...' : 'Eliminar'}
              </BaseButton>
              <BaseButton variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </BaseButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalles */}
      {showDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText size={18} className="text-blue-600" />
                Detalles del Informe
              </h3>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Veredicto */}
              {(() => {
                const vStyle = getVeredictoStyle(selectedReport.estado);
                const VIcon = vStyle.Icon;
                return (
                  <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border ${vStyle.bg} ${vStyle.text} ${vStyle.border}`}>
                    <VIcon size={14} />
                    {selectedReport.estado}
                  </span>
                );
              })()}

              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Nombre del Informe</label>
                <p className="text-gray-900 font-semibold text-sm break-all">{selectedReport.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Plantilla</label>
                  <p className="text-gray-800 text-sm">{selectedReport.plantilla}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Fecha</label>
                  <p className="text-gray-800 text-sm">{selectedReport.fechaEjecucion}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Generado por</label>
                  <p className="text-gray-800 text-sm">{selectedReport.createdBy}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Total validaciones</label>
                  <p className="text-gray-800 text-sm font-medium">{selectedReport.totalValidaciones}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3 flex flex-row-reverse gap-3 rounded-b-lg">
              {!isUsingMockData && (
                <BaseButton
                  variant="primary"
                  onClick={() => { handleDownload(selectedReport, true); setShowDetailModal(false); }}
                  disabled={downloadingId === selectedReport.id}
                >
                  <Eye size={15} className="mr-1" />
                  Ver PDF
                </BaseButton>
              )}
              <BaseButton variant="outline" onClick={() => setShowDetailModal(false)}>
                Cerrar
              </BaseButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportComparisonModule;
